import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../../utils/supabaseClient";
import "../../styles/login-dropdown.css";
import { useNavigate } from "react-router-dom";
import { vbDecrypt } from "../../utils/functions";
import { setCurrentUser } from "../../utils/session";

// Dashboard sign-in checks the EXECUTIVES table first (Login Name + password).
// While executives are still being set up, order-app users (the old `users`
// table, signing in with mobile number) are accepted as a fallback so nobody
// is locked out. Set this to false once every dashboard user is an executive.
const ALLOW_LEGACY_USER_LOGIN = true;

// Escape LIKE wildcards so a login name such as "ravi_k" matches literally.
const escapeLike = (s) => s.replace(/[\\%_]/g, "\\$&");

// Simple inline SVG icons to avoid lucide dependency issues
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const LoginDropdown = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  // Prevent body scroll when open on mobile
  useEffect(() => {
    if (open && window.innerWidth <= 520) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const login = async () => {
    if (!email.trim() || !password.trim()) {
      alert("Please enter your credentials.");
      return;
    }
    setLoading(true);
    try {
      const loginValue = email.trim();

      // 1) Executive: Login Name (case-insensitive) + password
      const { data: execRows, error: execErr } = await supabase
        .from("executives")
        .select("*")
        .ilike("login_name", escapeLike(loginValue))
        .limit(1);

      if (!execErr && execRows && execRows.length > 0) {
        const executive = execRows[0];
        const plain = await vbDecrypt(executive.password);
        if (plain !== password) { alert("Invalid password."); return; }

        setCurrentUser({ executive_id: executive.executive_id, executive_name: executive.executive_name });
        setOpen(false);
        navigate("/dashboard");
        return;
      }

      if (!ALLOW_LEGACY_USER_LOGIN) {
        alert(execErr ? "Login failed: " + execErr.message : "User not found.");
        return;
      }

      // 2) Fallback: order-app user (mobile number + password) — previous behaviour
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("mobile_number", loginValue)
        .single();

      if (error || !data) { alert("User not found."); return; }

      const decryptedPassword = await vbDecrypt(data.password);
      if (decryptedPassword !== password) { alert("Invalid password."); return; }

      setCurrentUser({ user_id: data.user_id, user_name: data.user_name });
      setOpen(false);
      navigate("/dashboard");
    } catch (err) {
      alert("Login failed: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") login();
  };

  const sendResetLink = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) alert(error.message);
      else alert("Password reset link sent!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper" ref={ref}>
      {/* Trigger button */}
      <button
        className="login-icon-btn"
        onClick={() => setOpen(!open)}
        aria-label="Open login"
        aria-expanded={open}
      >
        <IconUser />
        <span style={{ fontSize: "13px" }}>Login</span>
      </button>

      {/* Backdrop for mobile */}
      {open && (
        <div
          className="login-backdrop"
          onClick={() => setOpen(false)}
        />
      )}

      {open && (
        <div className="login-dropdown" role="dialog" aria-label="Login form">
          {/* Drag handle – visible only on mobile via CSS */}
          <div className="login-dropdown-handle" />

          {/* Coloured header */}
          <div className="login-dropdown-header">
            <span className="login-dropdown-logo-text">
              <em>XQ</em>lusive
            </span>
            {mode === "login" ? (
              <>
                <div className="login-dropdown-title">Welcome back 👋</div>
                <div className="login-dropdown-subtitle">Sign in to your account to continue</div>
              </>
            ) : (
              <>
                <div className="login-dropdown-title">Reset Password</div>
                <div className="login-dropdown-subtitle">We'll send a link to your email</div>
              </>
            )}
          </div>

          {/* Form */}
          <div className="login-dropdown-body">
            {mode === "login" ? (
              <>
                <div className="login-field">
                  <label className="login-field-label">Mobile / Username</label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon"><IconUser /></span>
                    <input
                      type="text"
                      placeholder="Enter login name or mobile number"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field-label">Password</label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon"><IconLock /></span>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoComplete="current-password"
                      style={{ paddingRight: "44px" }}
                    />
                    <button
                      className="login-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <IconEyeOff /> : <IconEye />}
                    </button>
                  </div>
                </div>

                <button className="otp-btn" onClick={login} disabled={loading}>
                  {loading ? "Signing in…" : "Sign In"}
                  {!loading && <IconArrowRight />}
                </button>

                <div className="login-footer-row">
                  <span className="forgot" onClick={() => setMode("forgot")}>
                    Forgot password? <IconArrowRight />
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="login-reset-info">
                  <span style={{ flexShrink: 0, marginTop: "1px" }}><IconInfo /></span>
                  <span>Enter your email and we'll send a reset link to your inbox.</span>
                </div>

                <div className="login-field">
                  <label className="login-field-label">Email Address</label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon"><IconMail /></span>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <button className="otp-btn" onClick={sendResetLink} disabled={loading}>
                  {loading ? "Sending…" : "Send Reset Link"}
                  {!loading && <IconArrowRight />}
                </button>

                <div className="login-footer-row">
                  <span className="forgot" onClick={() => setMode("login")}>
                    ← Back to Sign In
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginDropdown;